import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Loader2, RefreshCw, AlertTriangle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const FIELDS = [
  { name: "fajr", label: "Fajr Iqamah", autoSynced: true },
  { name: "dhuhr", label: "Dhuhr Iqamah", autoSynced: true },
  { name: "asr", label: "Asr Iqamah", autoSynced: true },
  { name: "maghrib", label: "Maghrib Iqamah", autoSynced: true },
  { name: "isha", label: "Isha Iqamah", autoSynced: true },
  { name: "jumuah_khutbah", label: "Jumu'ah Khutbah", autoSynced: false },
  { name: "jumuah_iqamah", label: "Jumu'ah Iqamah", autoSynced: false },
];

function relativeTime(iso) {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  const diff = Date.now() - t;
  if (diff < 60_000) return "just now";
  if (diff < 3600_000) return `${Math.floor(diff / 60_000)} min ago`;
  if (diff < 86400_000) return `${Math.floor(diff / 3600_000)} hr ago`;
  return `${Math.floor(diff / 86400_000)} day(s) ago`;
}

export default function IqamahAdmin() {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const load = () => {
    api.get("/admin/iqamah-times").then((r) => setData(r.data)).catch(() => {});
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/iqamah-times", data);
      toast.success("Iqamah times updated");
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  const syncNow = async () => {
    setSyncing(true);
    try {
      const r = await api.post("/admin/iqamah-times/sync");
      const t = r.data?.times;
      toast.success(t ? `Synced from Masjidal ✓  (Fajr ${t.fajr} · Isha ${t.isha})` : "Synced ✓");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sync failed — check your internet & Masjidal masjid_id");
    } finally {
      setSyncing(false);
    }
  };

  if (!data) return <div className="text-ink/40">Loading…</div>;

  const lastSyncRel = relativeTime(data.last_synced_at);

  return (
    <div data-testid="admin-iqamah-page">
      <div className="mb-8">
        <div className="text-[10px] uppercase tracking-[0.22em] text-gold font-semibold">Manage</div>
        <h1 className="font-serif text-4xl mt-2 text-ink">Iqamah Times</h1>
        <p className="mt-2 text-ink/60 text-sm max-w-2xl">
          These times appear on the public prayer-times widget. The five daily Iqamahs
          can be auto-synced every night from the Masjidal monthly chart so you don't
          have to update them weekly. Jumu'ah times stay manual.
        </p>
      </div>

      {/* AUTO-SYNC PANEL */}
      <div
        data-testid="auto-sync-panel"
        className="rounded-3xl bg-gradient-to-br from-navy to-navy-900 text-cream p-6 md:p-8 mb-8 max-w-3xl relative overflow-hidden"
      >
        <div className="absolute inset-0 pattern-girih opacity-10 pointer-events-none" />
        <div className="relative flex flex-wrap items-start justify-between gap-5">
          <div className="min-w-0 flex-1">
            <div className="text-[10px] uppercase tracking-[0.25em] text-gold font-semibold">Auto-sync</div>
            <h2 className="font-serif text-2xl mt-1.5">Pull Iqamah from Masjidal nightly</h2>
            <p className="mt-2 text-cream/70 text-sm leading-relaxed">
              When ON, every day at 1:30 AM EST we fetch today's row from the
              Masjidal monthly chart and update the five daily Iqamah times below.
            </p>

            {/* Status pill */}
            <div className="mt-4 flex flex-wrap items-center gap-3 text-xs">
              {data.last_sync_error ? (
                <span data-testid="sync-status" className="inline-flex items-center gap-1.5 rounded-full bg-rose-500/20 text-rose-100 px-3 py-1 border border-rose-400/30">
                  <AlertTriangle size={12} /> Last sync failed
                </span>
              ) : data.last_synced_at ? (
                <span data-testid="sync-status" className="inline-flex items-center gap-1.5 rounded-full bg-gold/20 text-gold px-3 py-1 border border-gold/30">
                  <CheckCircle2 size={12} /> Last synced {lastSyncRel}
                </span>
              ) : (
                <span data-testid="sync-status" className="inline-flex items-center gap-1.5 rounded-full bg-cream/10 text-cream/70 px-3 py-1 border border-cream/15">
                  Not synced yet
                </span>
              )}
              {data.last_sync_error && (
                <span className="text-rose-200/80 text-[11px] truncate max-w-md" title={data.last_sync_error}>
                  {data.last_sync_error}
                </span>
              )}
            </div>
          </div>

          {/* Toggle + manual sync */}
          <div className="flex flex-col items-end gap-3 shrink-0">
            <label className="inline-flex items-center gap-3 cursor-pointer select-none">
              <span className="text-xs uppercase tracking-wider text-cream/85">{data.auto_sync ? "ON" : "OFF"}</span>
              <span className="relative inline-flex items-center">
                <input
                  type="checkbox"
                  checked={!!data.auto_sync}
                  onChange={(e) => setData({ ...data, auto_sync: e.target.checked })}
                  data-testid="iqamah-auto-sync-toggle"
                  className="sr-only peer"
                />
                <span className="w-11 h-6 rounded-full bg-cream/20 peer-checked:bg-gold transition-colors" />
                <span className="absolute left-0.5 top-0.5 w-5 h-5 rounded-full bg-cream peer-checked:translate-x-5 transition-transform" />
              </span>
            </label>

            <button
              onClick={syncNow}
              disabled={syncing}
              data-testid="iqamah-sync-now"
              className="inline-flex items-center gap-2 rounded-full bg-gold text-navy px-5 py-2.5 text-xs font-bold uppercase tracking-wider hover:bg-gold-600 disabled:opacity-60 transition-all"
            >
              {syncing ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
              Sync now
            </button>
          </div>
        </div>
      </div>

      {/* MANUAL FIELDS */}
      <div className="rounded-3xl bg-white border border-sand p-8 md:p-10 max-w-3xl">
        <div className="text-[10px] uppercase tracking-[0.22em] text-gold font-semibold mb-1">Edit manually</div>
        <h3 className="font-serif text-xl text-ink">Override times</h3>
        <p className="mt-1.5 text-sm text-ink/55">
          Use this if you want to adjust a time the auto-sync produced. Saving here will
          stay until the next nightly sync overwrites it (turn OFF auto-sync above to keep your edits permanent).
        </p>

        <div className="mt-6 grid md:grid-cols-2 gap-5">
          {FIELDS.map((f) => (
            <div key={f.name}>
              <label className="text-[10px] uppercase tracking-[0.22em] text-gold font-semibold flex items-center gap-2">
                {f.label}
                {f.autoSynced && data.auto_sync && (
                  <span className="text-[9px] tracking-normal normal-case bg-gold/15 text-navy px-1.5 py-0.5 rounded-full border border-gold/30">
                    auto-synced
                  </span>
                )}
              </label>
              <input
                type="text"
                value={data[f.name] ?? ""}
                onChange={(e) => setData({ ...data, [f.name]: e.target.value })}
                data-testid={`iqamah-${f.name}`}
                className="mt-2 w-full px-4 py-3 rounded-xl border border-sand focus:outline-none focus:border-navy"
              />
            </div>
          ))}
        </div>

        <button onClick={save} disabled={saving} data-testid="iqamah-save"
                className="mt-8 inline-flex items-center gap-2 rounded-full bg-navy text-cream px-8 py-3 text-sm font-semibold uppercase tracking-wider hover:bg-navy-900 disabled:opacity-60">
          {saving && <Loader2 size={14} className="animate-spin" />} Save changes
        </button>
      </div>
    </div>
  );
}
